import React from "react";
import { cn } from "../../lib/utils";
import { DashboardSideNav } from "./DashboardSideNav";
import type { RouteMap } from "../../pages/MainAppPage/types";
import { AdminRouteMap } from "@/pages/AdminAppPage/types";

const DASHBOARD_PAGES = [
  "dashboard",
  "products_home",
  "onboarding",
  "quotes",
  "seo",
  "features",
  "account",
  "settings",
  "billing",
  "org_members",
  "jobs",
  "job_new",
  "job_show",
  "credentials",
  "credential_new",
  "credential_show",
  "credentials_roster",
  "teammate_home",
] as const;

export type DashboardLayoutProps = {
  children: React.ReactNode;
  routes: RouteMap;
  adminRoutes: AdminRouteMap;
  admin?: boolean;
  canAccessBackend?: boolean;
  unreadQuoteCount?: number;
  incomingOrgInvitationCount?: number;
  hasWebsite?: boolean;
  hasJobSitePhotos?: boolean;
  hasCredentials?: boolean;
  /** Main dashboard only: lock `lg+` column to the viewport for flex height chains. */
  fillViewport?: boolean;
};

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
  routes,
  adminRoutes,
  admin = false,
  canAccessBackend = false,
  unreadQuoteCount = 0,
  incomingOrgInvitationCount = 0,
  hasWebsite = false,
  hasJobSitePhotos = false,
  hasCredentials = false,
  fillViewport = false,
}) => {
  return (
    <div className="flex min-h-dvh">
      <DashboardSideNav
        routes={routes}
        adminRoutes={adminRoutes}
        admin={admin}
        unreadQuoteCount={unreadQuoteCount}
        incomingOrgInvitationCount={incomingOrgInvitationCount}
        hasWebsite={hasWebsite}
        hasJobSitePhotos={hasJobSitePhotos}
        hasCredentials={hasCredentials}
        canAccessBackend={canAccessBackend}
      />

      <div
        className={cn(
          "flex-1 min-w-0",
          "md:ml-56",
          fillViewport
            ? "max-lg:scroll-pt-[calc(var(--ds-app-header-height)+var(--ds-dashboard-mobile-tabs-height,3.125rem))] lg:flex lg:flex-col lg:min-h-0 lg:h-dvh lg:max-h-dvh lg:overflow-hidden"
            : "sm:max-h-screen",
        )}
        data-dashboard-mobile-scroll-region
      >
        <main
          className={cn(
            "mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 ds-main-below-app-header pb-0 sm:pb-6",
            fillViewport
              ? "flex min-h-0 flex-1 flex-col lg:h-full lg:overflow-hidden"
              : "h-full",
          )}
        >
          {children}
        </main>
      </div>
    </div>
  );
};

export const isDashboardPage = (
  page: string,
): page is (typeof DASHBOARD_PAGES)[number] =>
  DASHBOARD_PAGES.includes(page as (typeof DASHBOARD_PAGES)[number]);
